'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface CustomSwitchProps {
  checked?: boolean
  onChange?: (checked: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  currentDataChanged?: boolean
  className?: string
  disabledClassName?: string
}

export function CustomSwitch({
  checked = false,
  className = '',
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  currentDataChanged = false,
  disabledClassName = '',
}: CustomSwitchProps) {
  const [isChecked, setIsChecked] = useState(checked)

  const handleToggle = () => {
    if (disabled) return
    const newValue = !isChecked
    setIsChecked(newValue)
    onChange?.(newValue)
  }

  useEffect(() => {
    if (!currentDataChanged) {
      setIsChecked(checked)
    }
  }, [checked])

  const sizeClasses = {
    sm: {
      track: 'w-8 h-4',
      thumb: 'w-3 h-3',
      translateX: 16, // 4 * 4px (translate-x-4)
    },
    md: {
      track: 'w-11 h-6',
      thumb: 'w-5 h-5',
      translateX: 20, // 5 * 4px (translate-x-5)
    },
    lg: {
      track: 'w-14 h-7',
      thumb: 'w-6 h-6',
      translateX: 28, // 7 * 4px (translate-x-7)
    },
  }

  const currentSize = sizeClasses[size]

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <button
        type='button'
        role='CustomSwitch'
        aria-checked={isChecked}
        onClick={handleToggle}
        disabled={disabled}
        className={`
          relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          ${currentSize.track}
          ${isChecked ? 'bg-primary' : 'bg-gray-200'}
          ${disabled ? cn('opacity-50 cursor-not-allowed', disabledClassName) : 'cursor-pointer'}
        `}
      >
        <motion.span
          className={`
            inline-block rounded-full bg-white shadow-lg
            ${currentSize.thumb}
          `}
          animate={{
            x: isChecked ? currentSize.translateX : 0,
          }}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30,
          }}
        />
      </button>

      {(label || description) && (
        <div className='flex-1'>
          {label && (
            <label
              className={`block text-sm font-medium text-gray-900 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
              onClick={handleToggle}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              className={`text-sm text-gray-500 ${disabled ? 'opacity-50' : ''}`}
            >
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
